import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Clickable from 'components/Common/Clickable'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { getCheckInCheckOutText } from 'components/Search/utils'
import ResponsiveImage from 'components/Common/ResponsiveImage'

const Container = styled(Clickable)`
  width: ${rem(240)};
  min-height: ${rem(110)};
  flex-shrink: 0;

  > * {
    height: 100%;
  }
`

const ThumbnailWrapper = styled.div`
  width: ${rem(44)};
  height: ${rem(58)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  flex-shrink: 0;
`

interface Props {
  itemId: string
  propertyName: string
  checkInDate?: string
  checkOutDate?: string
  occupancy?: App.Occupants
  imageId?: string
  selected?: boolean
  onClick: (itemId: string) => void
}

export default function ChatLandingUpcomingOrderTile({ itemId, propertyName, checkInDate, checkOutDate, occupancy, imageId, selected, onClick }: Props) {
  const intl = useIntl()
  const subtitle = useMemo(() => {
    const parts: Array<string> = []
    if (checkInDate && checkOutDate) {
      parts.push(getCheckInCheckOutText(checkInDate, checkOutDate))
    }
    if (occupancy?.adults) {
      const adultsLabel = intl.formatMessage(
        {
          id: 'hd1_9T_9cOUVixrwCqmW',
          defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
          description: 'Pluralised adult count label for upcoming booking tile on chat page',
        },
        { count: occupancy.adults },
      )
      parts.push(adultsLabel)
    }
    return parts.join(' · ')
  }, [checkInDate, checkOutDate, occupancy?.adults, intl])

  const yourBookingLabel = useMemo(() => intl.formatMessage({
    id: 'dNPOURpXZmUVjbqyPKX3',
    defaultMessage: 'Your booking',
    description: 'Category label for upcoming booking tile on the chat landing page',
  }), [intl])

  const handleClick = useCallback(() => {
    onClick(itemId)
  }, [onClick, itemId])

  return (
    <Container onClick={handleClick}>
      <Sheet background="neutral-eight" border={selected ? 'neutral-one' : 'neutral-five'} borderRadius="L" padding={12}>
        <Group direction="vertical" gap={12} verticalAlign="center" fullHeight>
          <CaptionTextBlock
            variant="small"
            weight="bold"
            format="uppercase"
            colour="neutral-three"
            startIcon={<LineCalendarIcon size="XS" />}
          >
            {yourBookingLabel}
          </CaptionTextBlock>
          <Group direction="horizontal" gap={12} verticalAlign="center" grow>
            {imageId && <ThumbnailWrapper>
              <ResponsiveImage id={imageId} alt={propertyName} fit="center" objectFit="cover" />
            </ThumbnailWrapper>}
            <Group direction="vertical" gap={2}>
              <BodyText variant="medium" weight="normal" lineClamp={2}>
                {propertyName}
              </BodyText>
              {subtitle && <Caption variant="medium" colour="neutral-three" lineClamp={1}>
                {subtitle}
              </Caption>}
            </Group>
          </Group>
        </Group>
      </Sheet>
    </Container>
  )
}
